import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AcceptIngest,
  IngestListFilters,
  IngestListResponse,
  IngestProposal,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Cache-key scope — companyId-scoped like every other domain.
const ingestKey = (companyId: string | undefined) => ['doc-ingest', companyId] as const;

// GET /companies/:companyId/doc-ingest/:id — rebuilt proposal w/ fresh docUrl.
export function useIngest(companyId: string | undefined, ingestId: string) {
  return useQuery({
    queryKey: [...ingestKey(companyId), ingestId],
    queryFn: () => api<IngestProposal>(`/companies/${companyId}/doc-ingest/${ingestId}`),
    enabled: !!companyId && !!ingestId,
    // The docUrl is a short-lived (300s) presigned URL; don't serve a stale one
    // from cache on remount — always refetch a fresh proposal for the review page.
    staleTime: 0,
  });
}

// GET /companies/:companyId/doc-ingest?<filters> — inbox list.
export function useIngestList(companyId: string | undefined, filters: IngestListFilters) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.channel) qs.set('channel', filters.channel);
  if (filters.vendor) qs.set('vendor', filters.vendor);
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.q) qs.set('q', filters.q);
  if (filters.take != null) qs.set('take', String(filters.take));
  if (filters.skip != null) qs.set('skip', String(filters.skip));
  const search = qs.toString();
  return useQuery({
    queryKey: [...ingestKey(companyId), 'list', filters],
    queryFn: () =>
      api<IngestListResponse>(`/companies/${companyId}/doc-ingest${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

// POST /companies/:companyId/doc-ingest (multipart) — upload → extract → proposal.
// Do NOT set Content-Type: the browser sets the multipart boundary for FormData.
// On success the inbox list is invalidated so the new doc appears.
export function useUploadIngest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return api<IngestProposal>(`/companies/${companyId}/doc-ingest`, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ingestKey(companyId) });
    },
  });
}

// POST /companies/:companyId/doc-ingest/:id/accept — assemble → create AP bill.
// Invalidates the ingest scope + ap-bills + journal-entries + dashboard since a
// POSTED accept books a bill + JV (PENDING_APPROVAL routes to 4-eyes).
export function useAcceptIngest(companyId: string | undefined, ingestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AcceptIngest) =>
      apiJson<
        AcceptIngest,
        { apBillId: string; status: 'POSTED' | 'PENDING_APPROVAL'; anomalyReasons: string[] }
      >('POST', `/companies/${companyId}/doc-ingest/${ingestId}/accept`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ingestKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['ap-bills', companyId] });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
    },
  });
}

// "ตรวจสอบใหม่" on the RD chip (UNKNOWN verdict) — re-run RD verify, then refresh
// the proposal so the chip flips to the resolved verdict. The backend re-runs RD
// verify on every GET /:id (getProposal returns a fresh `rdVerify`) and has NO
// dedicated recheck-rd endpoint, so we just invalidate the ingest query to refetch
// a freshly-verified proposal (the task's documented fallback). mutationFn resolves
// immediately so callers can await it for toast/disabled-button feedback.
export function useRecheckRdVerify(companyId: string | undefined, ingestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => Promise.resolve(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['doc-ingest', companyId, ingestId] }),
  });
}

// POST /companies/:companyId/doc-ingest/:id/reject — terminate a REVIEWING doc.
export function useRejectIngest(companyId: string | undefined, ingestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reason }: { reason?: string }) =>
      apiJson<{ reason?: string }, void>(
        'POST',
        `/companies/${companyId}/doc-ingest/${ingestId}/reject`,
        // Conditionally spread so we never write `reason: undefined` —
        // exactOptionalPropertyTypes forbids assigning undefined to an optional.
        reason !== undefined ? { reason } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ingestKey(companyId) });
    },
  });
}
